/**
 * COIL PRINT SHEET — the dimensioned drawing (`table.notes` cell).
 *
 * 1:1 port of `coil.selector.print.tmpl:694-1460`. The drawing is pure
 * markup + CSS: `common.css`'s `@media print` block styles
 * `.coil-rating-mode table.notes tr td div.note…` (compiled from
 * `sass/ui/popup/component_selector/rating_mode_print.scss`), so the class
 * names and element ORDER below are the contract — every `div`/`span`, including
 * the empty ones, positions a rule. Do not "tidy" them.
 *
 * Four variants, keyed exactly as legacy does:
 *   fluid                → `.note.water`
 *   evaporator + std     → `.note.evaporator`
 *   evaporator + dual    → `.note.evaporator.dual`
 *   steam                → `.note.standart-steam`
 *   steam_distributing   → `.note.distributing-steam`
 *
 * `hand` is the conn-hand option's `value` ('right' / 'left'), matching
 * legacy's `getConnHand()` (coil.selector.js:2125-2135), NOT the label RH/LH.
 */

import { formatDecimalDisplay } from '../formUtils';

/** `<span class="number_output" data-format="decimal">` — validateOutputs
 * (coil.selector.js:4550-4572) formats these post-render; we format up-front. */
const D = ({ v }: { v: unknown }) => (
  <span className="number_output" data-format="decimal">
    {formatDecimalDisplay(v)}
  </span>
);

const Size = ({ v }: { v: unknown }) => (
  <span className="size">
    <D v={v} />
  </span>
);

/** The hatched break marks down the left edge (identical in every variant;
 * legacy repeats the same 13 pairs inline four times).
 * ★ Legacy's SECOND pair carries `Q25,19`/`Q20,19` where the series says 20 —
 * a 1px control-point typo on a decorative squiggle. Generated uniformly here;
 * the rendered curve is indistinguishable. */
const DimensionOl = () => (
  <svg className="dimension-ol">
    {[2, 17, 32, 47, 62, 77, 92, 107, 122, 137, 152, 167, 182].map((y) => (
      <g key={y}>
        <path d={`M30,${y + 8}Q25,${y + 3} 30,${y}`} stroke="" strokeWidth="3" />
        <path
          d={`M30,${y + 10}Q20,${y + 3} 30,${y - 2}`}
          fill="none"
          stroke="#111"
          strokeWidth="0.8"
        />
      </g>
    ))}
  </svg>
);

/** The AIRFLOW arrow head — mirrored for a left-hand coil. */
const ArrowRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="20" height="30">
    <path d="M0 7 L 0 0 L 15 15" fill="none" stroke="#111" strokeWidth="0.8" />
    <path d="M0 23 L 0 30 L 15 15" fill="none" stroke="#111" strokeWidth="0.8" />
  </svg>
);

const ArrowLeft = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    version="1.1"
    width="20"
    height="30"
    className="svg-right"
  >
    <path d="M15 23 L 15 30 L 0 15" fill="none" stroke="#111" strokeWidth="0.8" />
    <path d="M15 7 L 15 0 L 0 15" fill="none" stroke="#111" strokeWidth="0.8" />
  </svg>
);

/** evaporator: the suction-line squiggles above the top-right connection. */
const EvapTopSvgs = ({ dual }: { dual?: boolean }) => (
  <>
    <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="100" height="100">
      {(dual
        ? ['M0,65 C7,58 11,68 11,63', 'M1,66 C5,55 13,64 13,63', 'M1,67 C11,58 13,72 13,63', 'M1,65 C6,56 11,66 11,63']
        : ['M0,65 C7,58 11,68 15,63', 'M1,66 C5,55 13,64 15,63', 'M1,67 C11,58 13,72 15,63', 'M1,65 C6,56 11,66 15,63']
      ).map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
    <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="50" height="78">
      {(dual
        ? ['M0,65 C12,41 30,43 19,60', 'M0,67 C10,43 15,45 19,62', 'M0,65 C13,45 26,52 23,62', 'M0,65 C13,47 18,67 24,62', 'M0,65 C13,50 16,69 22,62', 'M0,65 C16,52 19,72 22,62']
        : ['M0,65 C15,41 30,49 19,60', 'M0,67 C10,51 15,49 19,62', 'M0,65 C13,56 26,52 23,62', 'M0,65 C13,56 18,71 24,62', 'M0,65 C13,56 16,82 22,62', 'M0,65 C16,72 19,87 22,62']
      ).map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
    <div className="circle" />
  </>
);

/** evaporator: the distributor bundle on the return side. */
const EvapDistributor = ({ dual }: { dual?: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="50" height="100">
    {(dual ? ['M0 10 Q3 10 10 20 T20 70'] : []).map((d) => (
      <path key={d} id="example_shape" d={d} />
    ))}
    {[
      'M0 10 Q3 10 10 20 T20 65',
      'M0 10 Q3 10 10 20 T20 60',
      'M0 15 Q5 15 15 30 T22 60',
      'M0 20 Q7 20 15 35 T22 60',
      'M0 25 Q10 25 16 40 T20 60',
      'M0 30 Q13 30 20 41 T19 60',
      'M0 35 Q16 35 22 42 T17 60',
      'M0 40 Q18 40 20 43 T20 60',
      'M0 45 Q20 40 20 45 T20 60',
      'M26 60 L 16 60 L 19 70 L19 72 L22 72 L22 70 L26 60',
      'M22 69 L 18 69',
    ].map((d) => (
      <path key={d} id="example_shape" d={d} />
    ))}
  </svg>
);

/** evaporator: the TXV bulb at the bottom-right corner. */
const EvapBulb = ({ std }: { std?: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    version="1.1"
    width="50"
    height="100"
    className={std ? 'evaporator-std' : undefined}
  >
    <path id="example_shape" d="M26 75 L 16 75 L 19 85 L19 87 L22 87 L22 85 L26 75" />
    <path id="example_shape" d="M22 84 L 18 84" />
  </svg>
);

export interface CoilPrintDrawingProps {
  coilType: string;
  /** evaporator style value — 'std' or the dual variant. */
  style: string;
  /** conn-hand option VALUE: 'right' | 'left'. */
  hand: string;
  depth: unknown;
  casingHeight: unknown;
  overallLength: unknown;
  fh: unknown;
  fl: unknown;
  flangeTop: unknown;
  flangeHeaderSide: unknown;
  flangeReturnSide: unknown;
  flangeBtm: unknown;
  casingLength: unknown;
}

/**
 * The shared skeleton: every variant is the same three rows (top edge with the
 * coil depth, the AIRFLOW middle band, the dimensioned bottom) with different
 * connection furniture. `extras` injects the per-variant pieces at the exact
 * positions legacy has them.
 */
const NoteBlock = ({
  className,
  hand,
  middleFlipOn,
  topRight,
  bottomExtras,
  afterRightDouble,
  bottomRight,
  p,
}: {
  className: string;
  hand: string;
  /** which hand mirrors the middle band (fluid/evaporator: 'left'; steam: 'right'). */
  middleFlipOn: 'left' | 'right';
  topRight: React.ReactNode;
  bottomExtras?: React.ReactNode;
  afterRightDouble?: React.ReactNode;
  bottomRight: React.ReactNode;
  p: CoilPrintDrawingProps;
}) => (
  <div className={`note ${className}`}>
    <div className="note-top">
      <div className="left" />
      <div className="center">
        <Size v={p.depth} />
      </div>
      <div className="right" />
      {topRight}
    </div>
    <div className={`middle ${hand === middleFlipOn ? ' middle-right ' : ''}`}>
      <span>AIRFLOW</span>
      {hand === 'right' ? <ArrowRight /> : <ArrowLeft />}
    </div>
    <div className="note-bottom">
      <DimensionOl />
      <div className="left">
        <Size v={p.casingHeight} />
      </div>
      <Size v={p.flangeTop} />
      <div className="top top-ol">
        <Size v={p.overallLength} />
      </div>
      <div className="top">
        <Size v={p.fl} />
      </div>
      <Size v={p.flangeHeaderSide} />
      <div className="right">
        <Size v={p.fh} />
      </div>
      <Size v={p.flangeReturnSide} />
      <div className="right double" />
      {afterRightDouble}
      {bottomExtras}
      <div className="bottom">
        <Size v={p.casingLength} />
      </div>
      <Size v={p.flangeBtm} />
      {bottomRight}
    </div>
  </div>
);

export const CoilPrintDrawing = (props: CoilPrintDrawingProps) => {
  const { coilType, style, hand } = props;
  const right = hand === 'right';

  if (coilType === 'fluid') {
    // tmpl:698-837 — `.note.water`.
    return (
      <NoteBlock
        className="water"
        hand={hand}
        middleFlipOn="left"
        p={props}
        topRight={
          <>
            <div className="right-top">
              <div className="circle" />
            </div>
            <div className="right-bottom">
              <div className="circle" />
            </div>
          </>
        }
        afterRightDouble={
          <>
            <div className="double-top" />
            <div className="double-bottom" />
          </>
        }
        bottomRight={
          <div className="bottom-right">
            <span>{right ? 'RH' : 'LH'}</span>
            {right ? (
              <>
                <div className="left-top">
                  <div className="circle-top" />
                </div>
                <div className="right-top">
                  <div className="circle-bottom" />
                </div>
              </>
            ) : (
              <>
                <div className="right-top">
                  <div className="circle-top" />
                </div>
                <div className="left-top">
                  <div className="circle-bottom" />
                </div>
              </>
            )}
          </div>
        }
      />
    );
  }

  if (coilType === 'evaporator') {
    const dual = style !== 'std';
    // tmpl:841-1002 (std) / :1004-1198 (dual).
    return (
      <NoteBlock
        className={dual ? 'evaporator dual' : 'evaporator'}
        hand={hand}
        middleFlipOn="left"
        p={props}
        topRight={
          <>
            <div className="right-top">
              <EvapTopSvgs dual={dual} />
            </div>
            <div className="right-bottom">
              <div className="circle" />
            </div>
            {dual && (
              <div className="right-bottom dual">
                <div className="circle" />
              </div>
            )}
          </>
        }
        afterRightDouble={
          <>
            <EvapDistributor dual={dual} />
            <div className="double-bottom" />
          </>
        }
        bottomRight={
          <div className="bottom-right">
            <span>{right ? 'RH' : 'LH'}</span>
            {right ? (
              <>
                <div className={`left-top${dual ? ' dual' : ''}`}>
                  <div className="circle-top" />
                </div>
                {dual && (
                  <div className="left-top dual">
                    <div className="circle-top " />
                  </div>
                )}
                <EvapBulb />
                {dual && <EvapBulb />}
              </>
            ) : (
              <>
                <div className={`right-top${dual ? ' dual' : ''}`}>
                  <div className="circle-bottom" />
                </div>
                {dual && (
                  <div className="right-top dual">
                    <div className="circle-bottom " />
                  </div>
                )}
                <EvapBulb std />
                {dual && <EvapBulb std />}
              </>
            )}
          </div>
        }
      />
    );
  }

  if (coilType === 'steam') {
    // tmpl:1202-1340 — `.note.standart-steam`. NOTE the middle band mirrors on
    // the RIGHT hand here, the opposite of fluid/evaporator (tmpl:1215).
    return (
      <NoteBlock
        className="standart-steam"
        hand={hand}
        middleFlipOn="right"
        p={props}
        topRight={
          <>
            <div className="right-top">
              <div className="circle" />
            </div>
            <div className="right-bottom">
              <div className="circle" />
            </div>
          </>
        }
        afterRightDouble={
          <>
            <div className="double-top" />
            <div className="double-bottom" />
          </>
        }
        bottomRight={
          <div className="bottom-right">
            <span>{right ? 'RH' : 'LH'}</span>
            {right ? (
              <>
                <div className="left-top">
                  <div className="circle-center" />
                </div>
                <div className="right-top">
                  <div className="circle-bottom" />
                </div>
              </>
            ) : (
              <>
                <div className="left-top">
                  <div className="circle-bottom " />
                </div>
                <div className="right-top">
                  <div className="circle-center" />
                </div>
              </>
            )}
          </div>
        }
      />
    );
  }

  if (coilType === 'steam_distributing') {
    // tmpl:1343-1459 — `.note.distributing-steam`: ONE top connection, the
    // AIRFLOW arrow is never mirrored, and the corner carries two circles.
    return (
      <div className="note distributing-steam">
        <div className="note-top">
          <div className="left" />
          <div className="center">
            <Size v={props.depth} />
          </div>
          <div className="right" />
          <div className="right-top">
            <div className="circle" />
          </div>
        </div>
        <div className="middle">
          <span>AIRFLOW</span>
          <ArrowRight />
        </div>
        <div className="note-bottom">
          <DimensionOl />
          <div className="left">
            <Size v={props.casingHeight} />
          </div>
          <Size v={props.flangeTop} />
          <div className="top top-ol">
            <Size v={props.overallLength} />
          </div>
          <div className="top">
            <Size v={props.fl} />
          </div>
          <Size v={props.flangeHeaderSide} />
          <div className="right">
            <Size v={props.fh} />
          </div>
          <Size v={props.flangeReturnSide} />
          <div className="right double" />
          <div className="double-top" />
          <div className="double-bottom" />
          <div className="bottom">
            <Size v={props.casingLength} />
          </div>
          <Size v={props.flangeBtm} />
          <div className="bottom-right">
            <div className="right-top">
              <div className="circle-middle" />
              <div className="circle-bottom" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // condenser / anything else: legacy has no drawing branch — the cell is empty.
  return null;
};

export default CoilPrintDrawing;
